import { FC, ReactNode, useEffect, useState } from "react";
import { useLocalSearchParams } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { CircleCheckBigIcon, PhoneIcon } from "lucide-react-native";
import { useForm } from "react-hook-form";

import { redeterminationStatusEnumSchema } from "@libs/api/patients";
import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import { PressableOption } from "@/blocks/PressableOption";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { UpdateHouseholdAddressesDialog } from "@/blocks/UpdateHouseholdAddressesDialog";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdateMedicaidCaseNumbersMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateRedeterminationMutation } from "@/data/mutations/redeterminationsMutations";
import {
  useCurrentHouseholdMemberQuery,
  useListHouseholdMembersQuery,
} from "@/data/queries/householdMembersQueries";
import { useGetRedeterminationQuery } from "@/data/queries/redeterminationsQueries";
import {
  caseNumberFormSchema,
  CaseNumberFormType,
  RedeterminationStatusType,
  RedeterminationStepType,
} from "@/domain/redeterminations";
import { useUpdateHouseholdAddressesDialog } from "@/hooks/useUpdateHouseholdAddressesDialog";
import { dateAsDayjs } from "@/utils/date";

import { HouseholdMemberAddressForm } from "./HouseholdMemberAddressForm";

type ChooseRedeterminationStatusPropsType = {
  onSelectedOption: ({
    status,
    step,
  }: {
    status: RedeterminationStatusType;
    step: RedeterminationStepType;
  }) => void;
};

const ChooseRedeterminationStatus = ({
  onSelectedOption,
}: ChooseRedeterminationStatusPropsType) => {
  const { id } = useLocalSearchParams<{
    id: string;
  }>();

  const { data: redetermination, isPending: isLoadingRedetermination } =
    useGetRedeterminationQuery(id);

  if (isLoadingRedetermination || !redetermination || !id) {
    return (
      <ScreenWrapper>
        <VnLoadingSpinner />
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <VnText
        variant="headerTwo"
        i18nKey="native:redeterminationScreen.title"
        i18nOptions={{
          date: dateAsDayjs(redetermination.redeterminationDate).format(
            "MMM D",
          ),
        }}
      />
      <VnText
        variant="lg"
        i18nKey="native:redeterminationScreen.description"
        className="text-muted-foreground mt-2"
      />
      <VnBox className="mt-6 gap-3">
        <PressableOption
          onPress={() =>
            onSelectedOption({
              status: redeterminationStatusEnumSchema.enum.PENDING_FORM,
              step: "CONFIRM_ADDRESS",
            })
          }
          optionI18nKey="native:redeterminationScreen.haventReceivedForm"
          isSelected={false}
          className="border-border shadow-border border shadow-sm"
        />
        <PressableOption
          onPress={() =>
            onSelectedOption({
              status: redeterminationStatusEnumSchema.enum.CALL_BACK,
              step: "CASE_NUMBER",
            })
          }
          isSelected={false}
          optionI18nKey="native:redeterminationScreen.receivedForm"
          className="border-border shadow-border border shadow-sm"
        />
        <PressableOption
          onPress={() =>
            onSelectedOption({
              status: redeterminationStatusEnumSchema.enum.COMPLETED,
              step: "SUCCESS",
            })
          }
          isSelected={false}
          optionI18nKey="native:redeterminationScreen.alreadyMailedForm"
          className="border-border shadow-border border shadow-sm"
        />
      </VnBox>
    </ScreenWrapper>
  );
};

type ConfirmAddressPropsType = {
  onSubmit: () => void;
  onBack: () => void;
};

const ConfirmAddress = ({ onSubmit, onBack }: ConfirmAddressPropsType) => {
  const { data: householdMembers } = useListHouseholdMembersQuery();
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const { handlePrimaryApplicantSaveSuccess, dialogProps } =
    useUpdateHouseholdAddressesDialog();

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  return (
    <>
      <CoverageEventsFlowStepWrapper
        onBack={onBack}
        onNext={onSubmit}
        nextButtonI18nKey="common:next">
        <VnText
          variant="headerTwo"
          i18nKey="native:redeterminationScreen.addressInformation"
        />
        <VnText
          variant="lg"
          i18nKey="native:redeterminationScreen.reviewAddress"
          className="text-muted-foreground mb-6 mt-2"
        />
        <VnBox className="gap-4">
          {householdMembers?.map(member => {
            const isExpanded = expandedMemberId === member.id;

            return (
              <HouseholdMemberAddressForm
                key={member.id}
                member={member}
                isExpanded={isExpanded}
                onToggleExpand={() => handleToggleExpand(member.id)}
                onClose={handleClose}
                onPrimaryApplicantSaveSuccess={
                  handlePrimaryApplicantSaveSuccess
                }
              />
            );
          })}
        </VnBox>
      </CoverageEventsFlowStepWrapper>

      <UpdateHouseholdAddressesDialog {...dialogProps} />
    </>
  );
};

type CaseNumberPropsType = {
  statusToSave: RedeterminationStatusType;
  onSubmit: () => void;
  onBack: () => void;
};

const CaseNumber = (props: CaseNumberPropsType) => {
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();

  if (!currentHouseholdMember) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={props.onBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  return (
    <CaseNumberForm
      {...props}
      householdMemberId={currentHouseholdMember.id}
      savedCaseNumber={currentHouseholdMember.medicaidCaseNumber ?? ""}
    />
  );
};

const CaseNumberForm = ({
  statusToSave,
  householdMemberId,
  savedCaseNumber,
  onSubmit,
  onBack,
}: CaseNumberPropsType & {
  householdMemberId: string;
  savedCaseNumber: string;
}) => {
  const { mutate: updateMedicaidCaseNumbers, isPending } =
    useUpdateMedicaidCaseNumbersMutation();

  const form = useForm<CaseNumberFormType>({
    defaultValues: { caseNumber: savedCaseNumber },
    resolver: standardSchemaResolver(caseNumberFormSchema),
  });

  const isPendingForm =
    statusToSave === redeterminationStatusEnumSchema.enum.PENDING_FORM;

  const handleNext = () => {
    void form.handleSubmit(({ caseNumber }) => {
      const trimmedCaseNumber = caseNumber?.trim() ?? "";

      // Compare with what the form showed: `savedCaseNumber` follows refetches,
      // so it would overwrite an unedited number that changed elsewhere.
      if (trimmedCaseNumber === form.formState.defaultValues?.caseNumber) {
        onSubmit();
        return;
      }

      updateMedicaidCaseNumbers(
        [
          {
            id: householdMemberId,
            medicaidCaseNumber: trimmedCaseNumber || null,
          },
        ],
        { onSuccess: onSubmit },
      );
    })();
  };

  return (
    <CoverageEventsFlowStepWrapper
      onBack={onBack}
      onNext={handleNext}
      nextButtonI18nKey={isPendingForm ? "common:submit" : "common:next"}
      isNextButtonLoading={isPending}>
      <VnText
        variant="headerTwo"
        i18nKey="native:redeterminationScreen.caseNumberTitle"
      />
      <VnText
        variant="lg"
        i18nKey="native:redeterminationScreen.caseNumberDescription"
        i18nOptions={{ context: statusToSave }}
        className="text-muted-foreground mb-6 mt-2"
      />
      <VnCard>
        <VnForm form={form} isInputOnlyForm>
          <VnTextInput
            name="caseNumber"
            control={form.control}
            isRequired={false}
            labelI18nKey="native:redeterminationScreen.caseNumberLabel"
            placeholderI18nKey="native:redeterminationScreen.caseNumberPlaceholder"
          />
        </VnForm>
      </VnCard>
    </CoverageEventsFlowStepWrapper>
  );
};

const ScreenWrapper = ({ children }: { children: ReactNode }) => (
  <ScreenLayout
    className="mx-auto w-full max-w-lg gap-2 py-6"
    backgroundClassName="bg-background">
    {children}
  </ScreenLayout>
);

type SuccessScreenPropsType = {
  statusToSave?: RedeterminationStatusType;
  redeterminationId: string;
};

const SuccessScreen: FC<SuccessScreenPropsType> = ({
  statusToSave,
  redeterminationId,
}) => {
  const { mutate: updateRedetermination } = useUpdateRedeterminationMutation();

  useEffect(() => {
    if (!redeterminationId || !statusToSave) return;

    updateRedetermination({
      id: redeterminationId,
      redeterminationData: {
        status: statusToSave,
      },
    });
  }, [redeterminationId, statusToSave, updateRedetermination]);

  return (
    <ScreenWrapper>
      <VnBox className="mt-32 items-center justify-center">
        <VnBox className="bg-chart-five items-center justify-center self-center rounded-full p-6">
          <VnIcon
            as={statusToSave === "CALL_BACK" ? PhoneIcon : CircleCheckBigIcon}
            size={42}
            className="text-white"
          />
        </VnBox>

        <VnText
          variant="headerOne"
          i18nKey="native:redeterminationScreen.successTitle"
          i18nOptions={{ context: statusToSave }}
          className="mt-8"
        />
        <VnText
          variant="lg"
          i18nKey="native:redeterminationScreen.successDescription"
          i18nOptions={{ context: statusToSave }}
          className="text-muted-foreground mt-4 text-center"
        />

        <VnLink
          href={{
            pathname: "/(authed)",
          }}
          i18nKey="common:returnToDashboard"
          renderAsButton
          className="mt-8 w-full"
        />
      </VnBox>
    </ScreenWrapper>
  );
};

const RedeterminationScreen = () => {
  const { id } = useLocalSearchParams<{
    id: string;
  }>();

  const [currentStep, setCurrentStep] =
    useState<RedeterminationStepType>("CHOOSING_STATUS");
  const [statusToSave, setStatusToSave] =
    useState<RedeterminationStatusType | null>(null);

  const handleBackToStatus = () => {
    setCurrentStep("CHOOSING_STATUS");
  };

  if (currentStep === "CONFIRM_ADDRESS") {
    return (
      <ConfirmAddress
        onSubmit={() => {
          setCurrentStep("CASE_NUMBER");
        }}
        onBack={handleBackToStatus}
      />
    );
  }

  if (currentStep === "CASE_NUMBER" && statusToSave) {
    const isAfterAddressStep =
      statusToSave === redeterminationStatusEnumSchema.enum.PENDING_FORM;

    return (
      <CaseNumber
        statusToSave={statusToSave}
        onSubmit={() => {
          setCurrentStep("SUCCESS");
        }}
        onBack={
          isAfterAddressStep
            ? () => setCurrentStep("CONFIRM_ADDRESS")
            : handleBackToStatus
        }
      />
    );
  }

  if (currentStep === "SUCCESS" && statusToSave) {
    return <SuccessScreen statusToSave={statusToSave} redeterminationId={id} />;
  }

  return (
    <ChooseRedeterminationStatus
      onSelectedOption={({ status, step }) => {
        setStatusToSave(status);
        setCurrentStep(step);
      }}
    />
  );
};

export { RedeterminationScreen };
